/**
 * Showing one screen in its own window, over the connection that already
 * exists.
 *
 * A popped-out screen used to be a second client: its own connection, its own
 * handshake, its own password, and a second entry in the machine's list of who
 * is connected. None of that was needed. The machine can already encode
 * several screens down one connection - that is what the all-screens view
 * uses - so the window that is already connected subscribes to the extra
 * screen and the new window simply lends it somewhere to draw.
 *
 * What crosses between the windows is an OffscreenCanvas, once, at the start.
 * After that the connected window draws straight into it and no video is
 * messaged anywhere. Only input comes back, and only when someone types or
 * moves the mouse.
 *
 * Both windows are the same origin - one opened the other - and every message
 * is checked against that before it is believed.
 */

/** Sent by the new window once it has a surface to offer. */
export interface MirrorReady {
  type: "rd-mirror-ready";
  peerId: string;
  display: number;
  canvas: OffscreenCanvas;
}

/** Sent by the connected window so the other can label itself. */
export interface MirrorInfo {
  type: "rd-mirror-info";
  display: number;
  hostname: string;
  width: number;
  height: number;
  /** Connection state of the one real session, mirrored for the title bar. */
  state: string;
  /** False when the operator has turned control off in the main window. */
  inputEnabled: boolean;
}

/**
 * A pointer or key from the new window.
 *
 * Coordinates are a fraction of the canvas rather than pixels, because the
 * two windows are different sizes and only the sender knows its own. The
 * connected window turns them into desktop coordinates using the position
 * the machine reported for that screen, which is the same arithmetic the
 * all-screens view already does for each of its panels.
 */
export interface MirrorInput {
  type: "rd-mirror-input";
  display: number;
  event:
    | { kind: "move"; nx: number; ny: number }
    | { kind: "down" | "up"; nx: number; ny: number; button: number }
    | { kind: "wheel"; nx: number; ny: number; dx: number; dy: number;
        pixels: boolean; ctrl: boolean }
    | { kind: "key"; down: boolean; code: string; key: string;
        ctrl: boolean; alt: boolean; shift: boolean; meta: boolean };
}

/** Sent when the new window is closing, or has been told to let go. */
export interface MirrorClosed {
  type: "rd-mirror-closed";
  display: number;
}

export type MirrorMessage = MirrorReady | MirrorInfo | MirrorInput | MirrorClosed;

/** Whether a message event came from our own origin and looks like ours. */
export function isMirrorMessage(e: MessageEvent): e is MessageEvent<MirrorMessage> {
  if (e.origin !== window.location.origin) return false;
  const d = e.data as { type?: unknown } | null;
  return !!d && typeof d === "object" && typeof d.type === "string"
    && d.type.startsWith("rd-mirror-");
}

/** The route a popped-out screen lives at. */
export const mirrorRoute = (peerId: string, display: number) =>
  `#/mirror/${encodeURIComponent(peerId)}?display=${display}`;

/**
 * Whether this browser can lend a canvas to another window.
 *
 * Without it there is no way to draw across windows cheaply, and popping out
 * falls back to opening a second session - which works, and costs a second
 * login.
 */
export const canMirror = (): boolean =>
  typeof HTMLCanvasElement !== "undefined"
  && typeof HTMLCanvasElement.prototype.transferControlToOffscreen === "function";
